import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URLS } from '../../config/api';

const JobSeekerList = () => {
    const navigate = useNavigate();
    const [jobSeekers, setJobSeekers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedEducation, setSelectedEducation] = useState('');
    const [selectedExperience, setSelectedExperience] = useState('');
    
    // State for unique values
    const [locations, setLocations] = useState([]);
    const [educationLevels, setEducationLevels] = useState([]);
    const [experienceLevels, setExperienceLevels] = useState([]);

    useEffect(() => {
        const fetchJobSeekers = async () => {
            try {
                console.log('Fetching job seekers from:', API_URLS.jobSeekers);
                const response = await fetch(API_URLS.jobSeekers);
                
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                console.log('Fetched job seekers:', data);
                setJobSeekers(data);
                
                // Extract unique values
                setLocations([...new Set(data.map(seeker => seeker.location))].filter(Boolean));
                setEducationLevels([...new Set(data.map(seeker => seeker.education))].filter(Boolean));
                setExperienceLevels([...new Set(data.map(seeker => seeker.experience))].filter(Boolean));
            } catch (err) {
                console.error('Fetch error details:', err);
                setError(err.message || 'Failed to fetch job seekers');
            } finally {
                setLoading(false);
            }
        };

        fetchJobSeekers();
    }, []);

    const filteredSeekers = jobSeekers.filter(seeker => {
        const searchFields = [
            seeker.name,
            seeker.email,
            seeker.phone,
            seeker.location,
            seeker.experience,
            seeker.education,
            seeker.skills,
            seeker.status
        ].map(field => (field || '').toLowerCase());

        const search = searchTerm.toLowerCase();
        const matchesSearch = searchTerm === '' || searchFields.some(field => field.includes(search));
            
        const matchesLocation = selectedLocation === '' || 
            seeker.location === selectedLocation;
            
        const matchesStatus = selectedStatus === '' || 
            seeker.status === selectedStatus;
            
        const matchesEducation = selectedEducation === '' || 
            seeker.education === selectedEducation;
            
        const matchesExperience = selectedExperience === '' || 
            seeker.experience === selectedExperience;
            
        return matchesSearch && matchesLocation && matchesStatus && 
               matchesEducation && matchesExperience;
    });

    const renderSkills = (skillsString) => {
        if (!skillsString) return null;
        return skillsString.split(',').map((skill, index) => (
            <span
                key={index}
                className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full mr-2 mb-2"
            >
                {skill.trim()}
            </span>
        ));
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this job seeker?')) {
            return;
        }

        setIsDeleting(true);
        try {
            const response = await fetch(API_URLS.deleteJobSeeker(id), {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to delete job seeker');
            }

            // Remove the deleted job seeker from the list
            setJobSeekers(prev => prev.filter(seeker => seeker.id !== id));
        } catch (err) {
            console.error('Delete error:', err);
            setError(err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (error) return <div className="p-8 text-red-600">Error: {error}</div>;

    return (
        <div className="p-8 bg-gray-50">
            <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Job Seekers</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Total Job Seekers: {jobSeekers.length}
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/admin/job-seekers/new')}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                        Add Job Seeker
                    </button>
                </div>

                {/* Enhanced Search and Filters */}
                <div className="bg-white p-4 rounded-lg shadow mb-6">
                    <div className="grid grid-cols-1 gap-4 mb-4">
                        <input
                            type="text"
                            placeholder="Search in all fields (name, email, skills, etc.)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <select
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        >
                            <option value="">All Locations</option>
                            {locations.map(location => (
                                <option key={location} value={location}>{location}</option>
                            ))}
                        </select>
                        
                        <select
                            value={selectedEducation}
                            onChange={(e) => setSelectedEducation(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        >
                            <option value="">All Education Levels</option>
                            {educationLevels.map(level => (
                                <option key={level} value={level}>{level}</option>
                            ))}
                        </select>
                        
                        <select
                            value={selectedExperience}
                            onChange={(e) => setSelectedExperience(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        >
                            <option value="">All Experience Levels</option>
                            {experienceLevels.map(level => (
                                <option key={level} value={level}>{level}</option>
                            ))}
                        </select>
                        
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Job Seekers Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Contact
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Skills
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Company Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Experience
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredSeekers.map((seeker) => (
                            <tr key={seeker.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{seeker.name}</div>
                                    <div className="text-sm text-gray-500">{seeker.location}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{seeker.email}</div>
                                    <div className="text-sm text-gray-500">{seeker.phone}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex flex-wrap">
                                        {renderSkills(seeker.skills)}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {seeker.company_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {seeker.experience}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        seeker.status === 'active' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        {seeker.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button 
                                        onClick={() => navigate(`/admin/job-seekers/${seeker.id}/edit`)}
                                        className="text-purple-600 hover:text-purple-900 mr-4"
                                    >
                                        Edit
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(seeker.id)}
                                        disabled={isDeleting}
                                        className="text-red-600 hover:text-red-900"
                                    >
                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default JobSeekerList; 